// External imports
import React, { useState, useRef, useEffect } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Internal imports
import { Button } from 'components/button/Button';
import { ELEMENTS } from 'providers/contentProvider/Elements';
import { useContentContext } from 'providers/contentProvider/ContentProvider';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import { usePaymentMethodCreation } from 'hooks/usePaymentMethodCreation/usePaymentMethodCreation';
import {
  COLOURS_AS_HEX, FEELIX_THEMES, PAYMENT_METHOD, REGIONS
} from '../../../constants';
import config from '../../../Config';
import { PaymentInfo } from './PaymentInfo';

// Types
type StripePaymentFormProps = {
  setPaymentMethodId: React.Dispatch<React.SetStateAction<string>>;
  onPaymentProfileSubmit: (isValidPaymentProfile: boolean) => void;
  onPaymentOptionChange: (option: PAYMENT_METHOD) => void;
  region: string;
  promoCode?: string;
  theme: string;
}
/**
 * CardError displays error messages in a styled div.
 */
const CardError: React.FC<{ msg: string }> = ({ msg }) => (
  <div style={{ width: '28em', color: COLOURS_AS_HEX.Berry, marginBottom: '2em', marginTop: '1em' }}>
    <span>{msg}</span>
  </div>
);

/**
 * Returns currency code for a given region.
 */
const getCurrency = (region: string): string => (
  {
    [REGIONS.AUSTRALIA]: 'aud',
    [REGIONS.NEW_ZEALAND]: 'nzd'
  }[region] || 'aud'
);

/**
 * InputForm handles Stripe payment form logic and rendering.
 */
const InputForm: React.FC<StripePaymentFormProps> =
  ({
    setPaymentMethodId,
    onPaymentProfileSubmit,
    onPaymentOptionChange,
    promoCode,
    region,
    theme
  }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [confirmationTokenId, setConfirmationTokenId] = useState('');
    const { getLabels } = useContentContext();
    const formRef = useRef<HTMLFormElement>(null);
    const { user } = useAuth0();
    const userEmail = user?.email || '';

    const handleChange = (event: any) => {
      const { value: { type } } = event;
      const option = type === 'card' ? PAYMENT_METHOD.CREDIT : PAYMENT_METHOD.DEBIT;
      onPaymentOptionChange(option);
    };

    // Button click triggers form submit
    const handleButtonClick = () => {
      formRef.current?.requestSubmit();
    };

    // Payment method creation hook
    const {
      isLoading: isPaymentMethodCreateLoading,
      paymentMethodType,
      paymentMethodId,
      isError,
      clientSecret,
      errorMessage: paymentMethodCreateErrorMsg
    } = usePaymentMethodCreation(
      confirmationTokenId,
      region,
      promoCode
    );

    useEffect(() => {
      const authenticatePayment = async () => {
        if (!clientSecret) return;
        if (!stripe || !elements) return;
        const {
          error,
          setupIntent
        } = await stripe.handleNextAction({
          clientSecret
        });
        if (error) {
          onPaymentProfileSubmit(false);
          setErrorMessage(error.message || 'Payment authentication failed.');
        } else {
          setPaymentMethodId(paymentMethodId!);
          onPaymentProfileSubmit(true);
        }
      }
      authenticatePayment().then(r => {
        // intentional no-op
      });
    }, [clientSecret, setErrorMessage]);

    // Handle payment method creation result
    useEffect(() => {
      if (clientSecret) return;
      if (paymentMethodId && paymentMethodType) {
        setPaymentMethodId?.(paymentMethodId);
        onPaymentProfileSubmit(true);
      } else if (isError) {
        onPaymentProfileSubmit(false);
        setErrorMessage(paymentMethodCreateErrorMsg || '');
      }
    }, [paymentMethodId, isError, paymentMethodCreateErrorMsg, clientSecret, paymentMethodType]);

    // Form submit handler
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!stripe || !elements) return;
      setIsLoading(true);
      setConfirmationTokenId('');
      setErrorMessage('');

      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Failed to load Stripe payment elements.');
        setIsLoading(false);
        return;
      }

      try {
        const { error, confirmationToken } = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              billing_details: { email: userEmail }
            }
          }
        });
        if (error) {
          setErrorMessage(error.message || 'Failed on confirmation token creation.');
          setIsLoading(false);
          return;
        }
        if (confirmationToken?.id) {
          setConfirmationTokenId(confirmationToken.id);
        } else {
          setErrorMessage('Failed to create confirmation token.');
        }
      } catch (err) {
        setErrorMessage('An unexpected error occurred.');
        console.error('Error:', err);
      }
      setIsLoading(false);
    };

    return (
      <form ref={formRef} onSubmit={handleSubmit}>
        <PaymentElement
          onChange={handleChange}
          options={{
          layout: { type: 'tabs', defaultCollapsed: false },
          paymentMethodOrder: ['cards'],
          fields: { billingDetails: { email: 'never', address: 'if_required' } }
          }}
        />
        {errorMessage && <CardError msg={errorMessage} />}
        <PaymentInfo theme={theme} />
        <div style={{ marginTop: '1em' }}>
          <Button
            data-testid='payment-submit-button'
            onClick={handleButtonClick}
            disabled={isLoading || !stripe || isPaymentMethodCreateLoading}
          >
            {getLabels(ELEMENTS.PAYMENT_FORM_SUBMIT_BUTTON)}
          </Button>
        </div>
      </form>
    );
  };

/**
 * StripePaymentForm wraps the Stripe Elements provider and renders the payment form.
 */
const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  setPaymentMethodId,
  onPaymentProfileSubmit,
  onPaymentOptionChange,
  promoCode,
  region = REGIONS.AUSTRALIA,
  theme = FEELIX_THEMES.MODERN
}) => {
  const options: StripeElementsOptions = {
    mode: 'setup',
    currency: getCurrency(region),
    paymentMethodCreation: 'manual',
    appearance: { theme: 'stripe' }
  };
  const key = region === REGIONS.AUSTRALIA ? config.STRIPE_PUBLIC_KEY_AU : config.STRIPE_PUBLIC_KEY_NZ;
  const stripePromise = loadStripe(key);

  return (
    <div
      data-testid='stripePaymentForm'
      id='stripePaymentForm'
      style={{ width: '25em', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}
    >
      <Elements stripe={stripePromise} options={options}>
        <InputForm
          setPaymentMethodId={setPaymentMethodId}
          onPaymentOptionChange={onPaymentOptionChange}
          onPaymentProfileSubmit={onPaymentProfileSubmit}
          promoCode={promoCode}
          region={region}
          theme={theme}
        />
      </Elements>
    </div>
  );
};

export default StripePaymentForm;